import React, { useCallback } from "react";
import { Controller } from "react-hook-form";
import { useDispatch } from "react-redux";

import { OverflowTooltip, SelectAsync, UserOptionLabel } from "components";
import { DebounceTimeout } from "globalConstants";
import { getFullName, getErrorMessage } from "helpers";
import { applicationListService } from "services";
import { uiActions } from "store";
import { Application, TableCellProp, ErrorResponse } from "types";

import styles from "../applicationList.module.scss";

const assignedToColumn = () => ({
	Header: "Assigned To",
	accessor: "assignedTo",
	disableSortBy: true,
	classNameCell: "editableColumn",
	Cell: ({ row, cellProps }: TableCellProp<Application>) => {
		const dispatch = useDispatch();

		const assignedTo = row.original?.assignee
			? getFullName(row.original?.assignee)
			: "Unassigned";

		const loadAssignedTo = useCallback(
			async (search, prevOptions, { page }) => {
				try {
					let newUsers = await applicationListService
						.getAssignedUsers?.(search, page)
						.catch((err: ErrorResponse) => {
							dispatch(
								uiActions.showErrorNotification({
									title: getErrorMessage(err),
								}),
							);
						});

					const hasMore =
						prevOptions.length + newUsers.items.length <
						newUsers.count;

					return {
						options: newUsers.items,
						hasMore,
						additional: {
							page: page + 1,
						},
					};
				} catch (error) {
					return { options: [] };
				}
			},
			[dispatch],
		);

		const TableSelect = useCallback(
			({ control }) => (
				<Controller
					render={({ field }) => (
						<SelectAsync
							{...field}
							loadOptions={loadAssignedTo}
							isSearchable
							cacheOptions
							defaultOptions
							loaderClassName="w-100"
							debounceTimeout={DebounceTimeout}
							getOptionLabel={UserOptionLabel}
							getOptionValue={option => option.id}
							className={styles.filterDropdown}
							placeholder="Assigned To"
							additional={{ page: 0 }}
						/>
					)}
					name="assignTo"
					control={control}
					defaultValue={null}
				/>
			),
			[loadAssignedTo],
		);

		return row.state.edit ? (
			<TableSelect control={cellProps.control} />
		) : (
			<OverflowTooltip onlyOnLaptop title={assignedTo}>
				{assignedTo}
			</OverflowTooltip>
		);
	},
});

export default assignedToColumn;
